import { useEffect, useLayoutEffect, useState } from 'react';
import { measureTopBar, topBarNodes } from '../utils/topBar';

/**
 * WHERE AN IN-PAGE STICKY BAR STICKS: just under whatever top bar is
 * covering the top of the viewport RIGHT NOW (Cowork run 51; R5 B1
 * review, 2026-10-26).
 *
 * There is no `--ff-navbar-height` (the bar's height depends on the width
 * and on what it holds), so it is MEASURED, through the one implementation
 * the dock and the navbar spacer already use (`utils/topBar`). Since R5 B1
 * the navbar stays at the top for the whole scroll (index.css: the shell
 * grows with the page), so a bar parked at a constant `top: 0` sits UNDER
 * it once the page scrolls — the racks phone bar lost back / title / "…"
 * that way. Every in-page sticky bar takes its `top` from this hook:
 *
 *   - BulkActionBar sets it as its inline `top`;
 *   - PhoneBar (racks phone views) and the cart's order summary set it as
 *     `--ff-sticky-top` on the bar, and their CSS reads
 *     `top: var(--ff-sticky-top, 0px)` (plus any gap of their own).
 *
 * It is re-read when the bar resizes (the navbar wraps at a narrow width),
 * on resize, and on scroll — the read is two DOM reads, and a layout that
 * carries the bar away (a fixed bar hidden, a shell clamped again) must
 * not leave a bar parked in mid-air. Scroll and resize are throttled to
 * one measurement per animation frame, and the setter is a no-op for an
 * equal value, so a scroll frame that changes nothing renders nothing.
 */
export default function useStickyTop(active = true) {
  const [top, setTop] = useState(0);
  useLayoutEffect(() => {
    if (active) setTop(measureTopBar());
  }, [active]);
  useEffect(() => {
    if (!active) return undefined;
    const measure = () => setTop(measureTopBar());
    // ONE measurement per frame (review of Cowork run 51): a scroll fires
    // many times a frame, and `measureTopBar` reads layout. The frame is
    // requested once and every event before it lands rides on it; the
    // cleanup cancels a frame still pending, so an unmounted bar never
    // sets state.
    const raf = typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame.bind(window) : null;
    let frame = null;
    const schedule = () => {
      if (!raf) { measure(); return; }
      if (frame !== null) return;
      frame = raf(() => { frame = null; measure(); });
    };
    let observer = null;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(schedule);
      topBarNodes().forEach((node) => observer.observe(node));
    }
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (observer) observer.disconnect();
      if (frame !== null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(frame);
      }
      frame = null;
    };
  }, [active]);
  return top;
}

/** The custom property a CSS-positioned sticky bar reads its `top` from. */
export const STICKY_TOP_VAR = '--ff-sticky-top';

/** `style` for a bar whose CSS says `top: var(--ff-sticky-top, 0px)`. */
export const stickyTopStyle = (top) => ({ [STICKY_TOP_VAR]: `${top}px` });

export { useStickyTop };
